import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { TranslationService } from '../../../../shared/translation/translation.service';
import { GaFormlyService, IExtraFieldConfigurationObject, IFieldOption } from '../../../../shared/formly/ga-formly.service';
import * as _ from 'underscore';
import { LovService } from '../../../../shared/services/lov.service';
import { ICodeValue, ILov, IValueObject } from '../../../../../type_definitions/datatypes';
import { FormattingService } from '../../../../shared/services/formatting.service';
import { UntypedFormGroup } from '@angular/forms';
import { OrderEntryWebshopService } from '../webshop/order-entry-webshop.service';
import { AuthService } from '../../../../shared/services/auth/auth.service';
import { GaMessageService } from '../../../../shared/message/ga-message.service';
import { IFileUpload, IFileUploadFailResponse } from '../../../../shared/file-upload/file-upload.service';
import { IDocumentGroup } from './order-entry-documents-data.service';

export interface IAddDocumentModel {
  name?:string;
  documentTypeHidden?:string;
  caveatDefinitionId?:number;
  otherDocumentType?:ICodeValue;
  documentType?:IDocumentTypeModel;
  documentId?:number;
}

export interface IDocumentTypeModel extends IValueObject {
  attachmentTypeId?:string;
  caveatDefinitionId?:number;
}

export const OTHER_DOCUMENTS_TYPE:string = 'other';

@Component({
  selector: 'order-entry-add-document-dialog',
  template: `
    <div class="message-dialog">
      <div class="modal-header">
        <h4 class="modal-title" translate>ORDERENTRY.DOCS.UPLOAD_FORM.TITLE</h4>
        <button type="button" class="btn-close" (click)="close()">
          <span class="visually-hidden" translate>GA.BUTTONS.CLOSE</span>
        </button>
      </div>

      <div class="modal-body">
        <formly [form]="form" [model]="model" [fields]="fields"></formly>
      </div>

      <div class="modal-footer">
        <file-button [class]="'btn btn-primary'" [url]="uploadPostUrl" [autoUpload]="true" (onFileAdd)="onFileAdd($event)" (onUploadStarted)="onUploadStarted()" (onUploadDone)="onUploadDone($event)" (onUploadFailed)="onUploadFailed($event)" [uploading]="saving">
          <i class="fa fa-folder-open-o"></i> {{'ORDERENTRY.DOCS.UPLOAD_FORM.ADD' | translate}}
        </file-button>

        <button class="btn btn-default col-margin-left" (click)="close()" translate>GA.BUTTONS.CANCEL</button>
      </div>
    </div>
  `
})
export class OrderEntryAddDocumentDialogComponent implements OnInit {
  @Input() public preselectedDocument:IDocumentGroup;
  @Input() public allowedDocuments:IFieldOption[];
  @Input() public orderId:string;

  @Output() public onNewDocumentAdded:EventEmitter<IAddDocumentModel> = new EventEmitter<IAddDocumentModel>();
  @Output() public onCancel:EventEmitter<void> = new EventEmitter<void>();

  public model:IAddDocumentModel = {};
  public fields:IExtraFieldConfigurationObject[] = [];
  public form:UntypedFormGroup = new UntypedFormGroup({});
  public saving:boolean;

  private otherDocumentsField:IExtraFieldConfigurationObject;
  private uploadProgressMessage:Noty;

  constructor(
    private lovService:LovService,
    private formlyService:GaFormlyService,
    private formattingService:FormattingService,
    private translationService:TranslationService,
    private orderEntryWebshopService:OrderEntryWebshopService,
    private authService:AuthService,
    private messageService:GaMessageService
  ) {
    /**/
  }

  public ngOnInit():void {
    this.model = {
      name: undefined,
      documentType: this.preselectedDocument? {
        id: this.isPreselectedOtherDocuments? OTHER_DOCUMENTS_TYPE : this.preselectedDocument.attachmentTypeId,
        value: this.preselectedDocument?.type
      } : undefined
    };

    if (this.preselectedDocument) {
      this.model.documentTypeHidden = this.preselectedDocument.attachmentTypeId;
      this.model.caveatDefinitionId = this.preselectedDocument.caveatDefinitionId;
    } else {
      // register listener if preselected is empty
      this.listen();
    }

    this.fields = this.createFields();
    this.fillFields();
  }

  private listen():void {
    this.form.valueChanges.subscribe((model:IAddDocumentModel) => {
      if (model?.documentType && model?.documentType?.id !== OTHER_DOCUMENTS_TYPE) {
        this.model.documentTypeHidden = model.documentType.attachmentTypeId;
        this.model.caveatDefinitionId = model.documentType.caveatDefinitionId;
      } else if (model?.otherDocumentType) {
        this.model.documentTypeHidden = model.otherDocumentType.code;
        this.model.caveatDefinitionId = null;
      } else {
        this.model.documentTypeHidden = null;
        this.model.caveatDefinitionId = null;
      }
    });
  }

  public close():void {
    this.onCancel.emit();
  }

  public get uploadPostUrl():string {
    const webshopId:number = this.orderEntryWebshopService.selectedShop.id;
    const countryId:number = this.orderEntryWebshopService.selectedCountry.id;
    const companyCountryId:number = this.orderEntryWebshopService.shopContext.bp.location.companyCountry.id;
    const orderId:string = this.orderId;

    let url:string = `/ga2/rest/orderentry/webshops/${webshopId}/countries/${countryId}/documents?companyCountryId=${companyCountryId}`;
    if (orderId) {
      url += '&orderId=' + orderId;
    }
    return this.authService.authenticatedUrl(url);
  }

  public onFileAdd(file:File):void {
    this.saving = false;
    if (!this.model.documentType) {
      this.messageService.showMessageDialog('ORDERENTRY.DOCS.MISSING_TYPE', 'ORDERENTRY.DOCS.SELECT_TYPE');
    } else {
      this.model.name = this.model.name || file.name;
    }
  }

  public onUploadStarted():void {
    this.saving = true;
    this.uploadProgressMessage = this.messageService.showStatusMessage(`Uploading Document`);
  }

  public onUploadDone(result:IFileUpload):void {
    this.saving = false;
    this.messageService.closeMessage(this.uploadProgressMessage);

    const documentId:number = <number>result.response.data;
    this.onNewDocumentAdded.emit({
      ...this.model,
      documentId
    });
    this.close();
  }

  public onUploadFailed(response:IFileUploadFailResponse):void {
    const failure:boolean = response.status === 'FAIL';

    if (failure) {
      this.messageService.closeMessage(this.uploadProgressMessage);
      this.saving = false;
      this.messageService.showErrorMessage(`Error uploading document: ${response.message}`);
      this.close();
    }
  }

  private get isPreselectedOtherDocuments():boolean {
    return this.preselectedDocument?.id === OTHER_DOCUMENTS_TYPE;
  }

  private createFields():IExtraFieldConfigurationObject[] {
    const fields:IExtraFieldConfigurationObject[] = [];
    const FULL_WIDTH:string = 'col-md-12';

    this.otherDocumentsField = this.formlyService.aSelect('otherDocumentType', this.preselectedDocument? 'Document Type' : undefined)
      .withFormat(this.formattingService.formatLov)
      .withLabelClass(!this.isPreselectedOtherDocuments? 'd-none' : undefined)
      .withValueClass(!this.isPreselectedOtherDocuments? FULL_WIDTH : undefined)
      .withHideExpression(() => {
        if (this.model.documentType?.id !== OTHER_DOCUMENTS_TYPE) {
          this.form.patchValue({ otherDocumentType: undefined });
          return true;
        }
      })
      .build();

    // Is there a type preselected?
    if (this.preselectedDocument) {
      fields.push(
        this.formlyService.aTitle(this.preselectedDocument.type)
          .withExtraOptions({
            templateOptions: {
              titleClass: 'form-title no-margin-top'
            }
          })
          .build()
      );

      if (this.isPreselectedOtherDocuments) {
        fields.push(this.otherDocumentsField);
      }
    } else {
      const documentTypeOptions:IFieldOption[] = this.allowedDocuments.length? _.clone(this.allowedDocuments) : [];
      const otherDocumentsOption:IFieldOption = {
        id: OTHER_DOCUMENTS_TYPE,
        value: 'Other Documents'
      };

      if (!_.findWhere(documentTypeOptions, { value: otherDocumentsOption.value })) {
        // add other documents
        documentTypeOptions.push(otherDocumentsOption);
      }

      // document types
      this.model.documentType = documentTypeOptions[ 0 ];

      fields.push(
        this.formlyService.aRadio('documentType', 'ORDERENTRY.DOCS.UPLOAD_FORM.TYPE')
          .isRequired()
          .withLabelClass(FULL_WIDTH)
          .withValueClass(`${FULL_WIDTH} col-space-top`)
          .withFormat(this.formattingService.formatLov)
          .withOptions(documentTypeOptions)
          .build(),
        this.otherDocumentsField
      );
    }

    fields.push(
      this.formlyService.aHidden('documentTypeHidden')
        .build(),
      this.formlyService.anInput('name', 'ORDERENTRY.DOCS.UPLOAD_FORM.NAME')
        .withLabelClass(FULL_WIDTH)
        .withValueClass(FULL_WIDTH)
        .withExtraOptions({
          name: 'doc_desc'
        })
        .build()
    );

    return fields;
  }

  private fillFields():void {
    // load other document types
    this.formlyService.fillField(
      this.otherDocumentsField,
      this.lovService.list('doctype')
        .then((types:ILov[]) => {
          return _.chain(types)
            .map(type => {
              type.id = (<any>type).code;
              return type;
            })
            .sortBy('description')
            .value();
        })
    );
  }
}